import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useGetClientsQuery } from "../api";
import { SelectedFilterList } from "../components/features/SelectedFilterList";
import { GoToActionBlock } from "../components/products";
import { InputWithQuery } from "../components/ui/InputWithQuery";
import { useStateWithSearchParams } from "../hooks";
import { ButtonsColorType } from "../types/enums";
import { QUERY_SEPARATOR } from "../constant";
import { AddNewClientBtn } from "../components/ui/buttons";
import {
  NavigationBreadcrumbs,
  PaginationWithQuery,
  ClientList,
  NoContentBlock,
} from "../components/ui";
import { useEffect, useState } from "react";

type SearchParams = {
  page?: string;
  per_page?: string;
  search?: string;
  company?: string;
};

const ClientsPage = () => {
  const { searchDict, setSearchValue } = useStateWithSearchParams<SearchParams>(
    {
      page: undefined,
      per_page: undefined,
      search: undefined,
      company: undefined,
    },
  );
  const [search, setSearch] = useState<string>(searchDict.search || "");

  const {
    data: clients,
    isLoading,
    isFetching,
  } = useGetClientsQuery(
    {
      page: searchDict?.page ? Number(searchDict?.page) + 1 : undefined,
      per_page: searchDict?.per_page ? Number(searchDict?.per_page) : undefined,
      search: search || undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (searchDict?.search !== search) {
      setSearchValue("page", undefined);
      setSearch(searchDict.search || "");
    }
  }, [searchDict?.search]);

  const removeFilterHandler = (key: string, filter: string) => {
    const filters = searchDict[key as keyof SearchParams];
    if (!filters) return;

    setSearchValue(
      key as keyof SearchParams,
      filters
        .split(QUERY_SEPARATOR)
        .filter((el) => el !== filter)
        .join(QUERY_SEPARATOR),
    );
  };

  const isNoData =
    (!!search && clients?.items.length === 0) ||
    (isFetching && clients?.items.length === 0);

  const isNoClients = !search && !isFetching && clients?.items.length === 0;

  const breadcrumbs = [
    {
      title: "Client User Management",
    },
  ];

  return (
    <Stack
      sx={{ px: { sm: 8, md: 8, lg: 10, xl: 20 }, pt: 4, pb: 6 }}
      gap="24px"
    >
      <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
      <Typography variant="h4" color="custom.textDarkBlue">
        Client User Management
      </Typography>
      <Stack direction="row" gap="8px">
        <Stack direction="row" gap="8px" width={"100%"} alignItems={"center"}>
          <InputWithQuery
            placeholder={"Search by name, email, company..."}
            maxWidth="528px"
            isDisabled={isNoClients}
          />
        </Stack>
        {!isNoClients && !isLoading && (
          <AddNewClientBtn buttonType={ButtonsColorType.blue} />
        )}
      </Stack>
      {searchDict.company && (
        <Stack direction="row" flexWrap="wrap" gap="8px">
          <SelectedFilterList
            filters={searchDict.company.split(QUERY_SEPARATOR) || []}
            label="Company"
            removeHandler={removeFilterHandler}
          />
        </Stack>
      )}

      {isFetching && !clients ? (
        <Box
          display="flex"
          height="25vh"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress size={80} sx={{ color: "#FE8836" }} />
        </Box>
      ) : (
        <>
          {isNoClients && (
            <GoToActionBlock
              bgColor={"#F7F7F7"}
              border={"none"}
              title="No client users added yet"
              texts={[
                "You haven’t added any users to Client User Management.",
                "Add your first user to grant access and start managing client users.",
              ]}
              btn={<AddNewClientBtn buttonType={ButtonsColorType.gradient} />}
            />
          )}

          {isNoData ? (
            <Stack
              gap="8px"
              alignItems="center"
              justifyContent="center"
              padding="48px"
            >
              <NoContentBlock
                title={"No Matching Clients"}
                text={
                  "No clients match the current filters. Try changing or clearing them to " +
                  "see more results."
                }
              />
            </Stack>
          ) : (
            <>
              <ClientList
                isNoClient={clients?.items.length === 0}
                clients={clients?.items || []}
              />
              {!isNoClients && (
                <PaginationWithQuery totalRows={clients?.count || 0} />
              )}
            </>
          )}
        </>
      )}
    </Stack>
  );
};

export default ClientsPage;
